import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Theme, Typography, useTheme } from '@mui/material';
import { BarSeriesType, LineSeriesType } from '@mui/x-charts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface CustomLegendProps {
  series: (LineSeriesType | BarSeriesType)[];
  rightAlignedIds?: string[];
}

const getLabel = (s: LineSeriesType | BarSeriesType): string => {
  if (typeof s.label === 'function') {
    return s.label('legend');
  }

  return s.label || s.dataKey || '';
};

const CustomLegendItem = ({ serie, theme}: { serie: LineSeriesType | BarSeriesType, theme: Theme}) => {
  const { t, i18n } = useTranslation();

  const label = getLabel(serie);

  const translatedLabel = i18n.exists(
    `screens.dashboarding.charts.${label}`
  )
    ? t(`screens.dashboarding.charts.${label}`)
    : label;

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        padding: 1,
        minHeight: 24,
      }}
    >
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: serie.color,
          flexShrink: 0,
          marginRight: 1,
        }}
      />
      <Typography
        style={theme.typography.lineBarText}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {translatedLabel}
      </Typography>
    </Stack>
  );
};

const CustomLegend = ({ series, rightAlignedIds = [] }: CustomLegendProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [legendLength, setLegendLength] = useState(10);

  const rightSeries = series.filter(s =>
    rightAlignedIds.includes(String(s.id))
  );

  const leftSeriesAll = series.filter(
    s => !rightAlignedIds.includes(String(s.id))
  );

  const leftLegendItems = leftSeriesAll.slice(0, legendLength);

  const showMore = legendLength < leftSeriesAll.length;

  const handleShowMore = useCallback(() => {
    setLegendLength(leftSeriesAll.length);
  }, [leftSeriesAll.length]);

  const handleShowLess = useCallback(() => {
    setLegendLength(10);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          marginLeft: '40px',
          alignItems: 'flex-start',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', flex: 1 }}>
          {leftLegendItems.map((s) => <CustomLegendItem serie={s} theme={theme} key={s.dataKey} />)}
        </Box>

        {rightSeries.length > 0 && (
          <Box sx={{ display: 'flex', flexShrink: 0, marginRight: '40px' }}>
            {rightSeries.map((s) => <CustomLegendItem serie={s} theme={theme} key={s.dataKey} />)}
          </Box>
        )}
      </Box>

      {leftSeriesAll.length > 10 && (
        <Box sx={{ marginLeft: '40px' }}>
          {showMore ? (
            <Button
              startIcon={<ExpandMoreIcon />}
              onClick={handleShowMore}
              style={theme.typography.tooltipText}
            >
              {t('actions.showMore')}
            </Button>
          ) : (
            <Button
              startIcon={<ExpandLessIcon />}
              onClick={handleShowLess}
              style={theme.typography.tooltipText}
            >
              {t('actions.showLess')}
            </Button>
          )}
        </Box>
      )}
    </>
  );
};

export default CustomLegend;
